import { useSearchParams } from 'next/navigation'
import * as React from 'react'

interface UseScrollHighlightProps {
  elementIdPrefix: string
  highlightDuration?: number
}

export const useScrollHighlight = ({ elementIdPrefix, highlightDuration = 2500 }: UseScrollHighlightProps) => {
  const [highlightedId, setHighlightedId] = React.useState<number | null>(null)
  const searchParams = useSearchParams()
  const shouldScroll = searchParams.get('scrollToComments') === 'true'
  const targetId = searchParams.get('commentId')

  React.useEffect(() => {
    if (shouldScroll && targetId) {
      let retryCount = 0
      const maxRetries = 5
      const retryInterval = 100 // ms

      const scrollToElement = () => {
        const element = document.getElementById(`${elementIdPrefix}-${targetId}`)
        if (element) {
          // Small delay to ensure proper rendering
          setTimeout(() => {
            // Ensure element is in viewport
            const elementRect = element.getBoundingClientRect()
            const absoluteElementTop = elementRect.top + window.pageYOffset
            window.scrollTo({
              top: absoluteElementTop - window.innerHeight / 2 + elementRect.height / 2,
              behavior: 'smooth',
            })

            setHighlightedId(+targetId)
            setTimeout(() => {
              setHighlightedId(null)
            }, highlightDuration)
          }, 100)
        } else if (retryCount < maxRetries) {
          retryCount++
          setTimeout(scrollToElement, retryInterval)
        }
      }

      scrollToElement()
    }
  }, [targetId, shouldScroll, elementIdPrefix, highlightDuration])

  return {
    highlightedId,
    isHighlighted: (id: number) => highlightedId === id,
  }
}
